import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
  IPublicClientApplication,
} from '@azure/msal-browser';

export interface SessionError {
  message: string;
  requiresInteraction: boolean;
}

interface SessionState {
  accountId: string | null;
  token: string | null;
  error: SessionError | null;
  busy: boolean;
}

export const accountKey = (account: AccountInfo | null) =>
  account
    ? `${account.homeAccountId}:${account.tenantId}:${account.localAccountId}`
    : null;

function describeError(error: unknown): SessionError {
  if (error instanceof InteractionRequiredAuthError) {
    return {
      message: 'Your session needs to be renewed. Sign in again to continue.',
      requiresInteraction: true,
    };
  }
  const code =
    typeof error === 'object' && error !== null && 'errorCode' in error
      ? error.errorCode
      : undefined;
  if (code === 'popup_window_error' || code === 'empty_window_error') {
    return {
      message:
        'Your browser could not open the sign-in window. Allow popups and try again, or sign in in this tab.',
      requiresInteraction: true,
    };
  }
  if (code === 'user_cancelled') {
    return {
      message: 'Sign-in was cancelled. Sign in again when you are ready.',
      requiresInteraction: true,
    };
  }
  return {
    message:
      'We could not renew your session. Check your connection and retry, or sign in again.',
    requiresInteraction: false,
  };
}

/** One session owns token acquisition and recovery for every admin API caller. */
export function createApiSession(
  instance: IPublicClientApplication,
  scopes: string[],
) {
  let account: AccountInfo | null = null;
  let inProgress: InteractionStatus = InteractionStatus.Startup;
  let generation = 0;
  let pending: Promise<string> | undefined;
  let interaction: Promise<void> | undefined;
  let state: SessionState = {
    accountId: null,
    token: null,
    error: null,
    busy: false,
  };
  const listeners = new Set<() => void>();
  const idleWaiters = new Set<() => void>();

  function update(changes: Partial<SessionState>) {
    const next = { ...state, ...changes };
    if (
      next.accountId === state.accountId &&
      next.token === state.token &&
      next.error === state.error &&
      next.busy === state.busy
    ) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function synchronize(
    nextAccount: AccountInfo | null,
    status: InteractionStatus,
  ) {
    if (accountKey(nextAccount) === accountKey(account)) {
      account = nextAccount;
    } else {
      generation++;
      pending = undefined;
      account = nextAccount;
      update({
        accountId: accountKey(account),
        token: null,
        error: null,
        busy: false,
      });
    }
    inProgress = status;
    if (status === InteractionStatus.None) {
      for (const resolve of idleWaiters) {
        resolve();
      }
      idleWaiters.clear();
    }
  }

  function accept(result: AuthenticationResult, startedAt: number) {
    if (
      startedAt !== generation ||
      accountKey(result.account) !== accountKey(account)
    ) {
      throw new Error('The signed-in account changed. Please try again.');
    }
    if (!result.accessToken) {
      throw new Error('No API access token was returned.');
    }
    update({ token: result.accessToken, error: null, busy: false });
    return result.accessToken;
  }

  async function getToken(forceRefresh = false): Promise<string> {
    const requestedFor = generation;
    if (inProgress !== InteractionStatus.None) {
      await new Promise<void>((resolve) => idleWaiters.add(resolve));
    }
    if (requestedFor !== generation) {
      throw new Error('The signed-in account changed. Please try again.');
    }
    if (pending) {
      return pending;
    }
    const startedAt = generation;
    if (!account) {
      const error = new InteractionRequiredAuthError('login_required');
      update({ token: null, error: describeError(error), busy: false });
      throw error;
    }
    if (!state.token || forceRefresh) {
      update({ busy: true });
    }
    const request = instance
      .acquireTokenSilent({ scopes, account, forceRefresh })
      .then((result) => accept(result, startedAt))
      .catch((error: unknown) => {
        if (startedAt === generation) {
          update({ token: null, error: describeError(error), busy: false });
        }
        throw error;
      })
      .finally(() => {
        if (pending === request) {
          pending = undefined;
        }
      });
    pending = request;
    return request;
  }

  function signIn(redirect = false): Promise<void> {
    if (interaction) {
      return interaction;
    }
    if (inProgress !== InteractionStatus.None) {
      return Promise.resolve();
    }
    const startedAt = generation;
    update({ busy: true });
    const request = {
      scopes,
      account: account ?? undefined,
      extraScopesToConsent: ['User.Read'],
    };
    // Start directly in the click handler, before any await, to preserve the
    // browser's user gesture for popup creation.
    const operation = redirect
      ? instance.acquireTokenRedirect(request)
      : instance.acquireTokenPopup(request).then((result) => {
          if (
            startedAt !== generation &&
            accountKey(result.account) !== accountKey(account)
          ) {
            throw new Error('The signed-in account changed. Please try again.');
          }
          if (!result.accessToken) {
            throw new Error('No API access token was returned.');
          }
          instance.setActiveAccount(result.account);
          synchronize(result.account, InteractionStatus.None);
          accept(result, generation);
        });
    interaction = operation
      .catch((error: unknown) => {
        if (startedAt === generation) {
          update({ token: null, error: describeError(error), busy: false });
        }
        throw error;
      })
      .finally(() => {
        interaction = undefined;
      });
    return interaction;
  }

  return {
    getToken,
    signIn,
    synchronize,
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    rejectToken(rejectedToken: string) {
      if (state.token !== rejectedToken) {
        return;
      }
      update({
        token: null,
        busy: false,
        error: {
          message: 'The API rejected your session. Sign in again to continue.',
          requiresInteraction: true,
        },
      });
    },
  };
}

export type ApiSession = ReturnType<typeof createApiSession>;
